import {Box, Grid, Typography} from '@mui/material';
import {GridSortModel} from '@mui/x-data-grid';
import {t} from 'i18next';
import {isEqual} from 'lodash';
import {useSelector} from 'react-redux';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ReactComponent as FolderIcon} from '../../../assets/images/icon-folder.svg';
import {
  PAGE_SIZE,
  redactionActions,
  redactionStatus,
  ToastType,
} from '../../../shared/constants';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {getProjectFieldListGridColumns} from './BatchRedationHistoryColumn';
import './BatchRedactionHistoryGrid.scss';
import {
  useBatchRedactionReport,
  useBatchRedactionRollback,
  useGetRedactionHistory,
} from '../../../hooks/redactions/useBatchRedaction';
import {
  IBatchRedactionReportRequest,
  IRedactionHistoryItem,
  IRedactionHistoryRequest,
} from '../../../api/types/redactions.type';
import ManageHeader from '../../components/manage-header/ManageHeader';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {RootState} from '../../../redux/store';

interface IBatchRedactionHistoryGridProps {
  refreshBatchRedactionHistory: boolean;
}

const BatchRedactionHistoryGrid: React.FC<IBatchRedactionHistoryGridProps> = ({
  refreshBatchRedactionHistory,
}) => {
  const singalrLastEvent = useSelector(
    (state: RootState) => state.signalr.lastEvent
  );

  const [pageNumber, setPageNumber] = useState(1);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'batchRedactionID',
      sort: 'desc',
    },
  ]);

  const [activeAction, setActiveAction] = useState<string | null>(null);

  const params = useMemo<IRedactionHistoryRequest>(
    () => ({
      displayLength: PAGE_SIZE,
      pageNumber,
      sortingColumn: sortModel[0]?.field ?? 'batchRedactionID',
      direction: sortModel[0]?.sort ?? 'asc',
    }),
    [pageNumber, sortModel]
  );

  const {data, isLoading, refetch} = useGetRedactionHistory(params);
  const {
    mutate: rollback,
    data: rollbackData,
    error: rollbackError,
    isPending: rollbackLoading,
    isError: rollbackIsError,
    isSuccess: rollbackIsSuccess,
    reset: resetRollbackState,
  } = useBatchRedactionRollback();
  const {
    mutate: generateReport,
    data: reportData,
    isSuccess: reportIsSuccess,
    isError: reportIsError,
    error: reportError,
  } = useBatchRedactionReport();

  const [confirmModal, setConfirmModal] = useState(false);
  const [cancelRollback, setCancelRollback] = useState(false);
  const [batchRedactionData, setBatchRedactionData] =
    useState<IRedactionHistoryItem | null>(null);
  const [isShowSuccessToast, setIsShowSuccessToast] = useState(false);
  const [isShowErrorToast, setIsShowErrorToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const redactionData = useMemo(() => data?.data ?? [], [data?.data]);

  const totalRecords = useMemo(
    () => data?.totalRecords ?? 0,
    [data?.totalRecords]
  );

  const gridWrapperRef = useRef<HTMLDivElement | null>(null);
  const isZeroState = !isLoading && totalRecords === 0;

  const handlePageChange = useCallback((newPage: number) => {
    setPageNumber(newPage);
  }, []);

  useEffect(() => {
    if (
      singalrLastEvent &&
      singalrLastEvent.payload?.type === 'BatchRedaction'
    ) {
      refetch();
    }
  }, [singalrLastEvent, refetch]);

  useEffect(() => {
    if (activeAction === redactionActions.Report && reportIsSuccess) {
      setIsShowSuccessToast(true);
      setActiveAction(null);
      setSuccessMessage(reportData?.message + '.');
      refetch();
      return;
    }

    if (activeAction === redactionActions.Rollback && rollbackIsSuccess) {
      setIsShowSuccessToast(true);
      setConfirmModal(false);
      refetch();
      setSuccessMessage(
        cancelRollback
          ? rollbackData?.message + '.'
          : t('batchRedactions.rollbackPopup.rollbackSuccessMsg')
      );
      resetRollbackState();
      setActiveAction(null);
    }
  }, [
    activeAction,
    rollbackIsSuccess,
    reportIsSuccess,
    refetch,
    cancelRollback,
    rollbackData?.message,
    reportData?.message,
    resetRollbackState,
  ]);

  useEffect(() => {
    if (activeAction === redactionActions.Report && reportIsError) {
      setIsShowErrorToast(true);
      return;
    }
    if (activeAction === redactionActions.Rollback && rollbackIsError) {
      setIsShowErrorToast(true);
      setConfirmModal(false);
    }
  }, [activeAction, rollbackIsError, reportIsError]);

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      if (!isEqual(sortModel, newSortModel)) {
        setSortModel(newSortModel);
        setPageNumber(1);
      }
    },
    [sortModel]
  );
  /**
   * No Rows UI
   */
  const NoRowsOverlay = useCallback(
    () => (
      <Box className="no-grid-overlay">
        <FolderIcon />
        <Typography
          className="typo-text headline3_size"
          data-testid={`batch-redaction-history-grid-message-for-no-grid-data`}
        >
          {t('manageCommentFields.messageForNoGridData')}
        </Typography>
        <Box className="typo-text body3_size no-grid-content">
          {t('batchRedactions.historyGrid.noData')}
        </Box>
      </Box>
    ),
    []
  );

  const handleClickActions = useCallback((data: IRedactionHistoryItem) => {
    setBatchRedactionData(data);
    if (data.status === redactionStatus.preprocessing && !data.isCancelled) {
      setCancelRollback(true);
    } else {
      setCancelRollback(false);
    }
    setConfirmModal(true);
  }, []);

  const handleGenerateReport = useCallback(
    (data: IRedactionHistoryItem) => {
      if (!data?.batchRedactionID) return;
      setActiveAction(redactionActions.Report);
      const payload: IBatchRedactionReportRequest = {
        batchRedactionId: data.batchRedactionID.toString(),
        actionCode: redactionActions.ActionCode,
        batchRedactionTag: data.redactionLabel,
        exportContextType: redactionActions.ExportContextType,
        batchRedactionTitle: data.batchRedactionLabel,
        isBatchRedactionRolledBack: data.rolledBack,
      };
      generateReport(payload);
    },
    [generateReport]
  );

  const closeConfirmModal = () => {
    setConfirmModal(false);
  };

  const rollbackErrorMessage = useMemo(() => {
    const raw = (
      activeAction === redactionActions.Report ? reportError : rollbackError
    ) as any;
    return raw?.response?.data?.detail?.split('\n')?.[0] || raw?.message || '';
  }, [rollbackError, reportError, activeAction]);

  const handleConfirmModal = () => {
    resetRollbackState();
    setActiveAction(redactionActions.Rollback);
    rollback({
      batchRedactionId: batchRedactionData?.batchRedactionID ?? '',
      cancelRollback,
    });
  };

  const formatDateTime = (value?: string): string => {
    try {
      if (!value) return '';
      const date = new Date(value);
      return date.toLocaleString('en-US').replace(',', '');
    } catch {
      return '';
    }
  };

  useEffect(() => {
    refreshBatchRedactionHistory && refetch();
  }, [refreshBatchRedactionHistory, refetch]);

  useEffect(() => {
    const liveRegionId = 'batch-redaction-history-toast-live-region';
    const container = document.querySelector(
      '[data-testid="redaction-history-grid"]'
    );

    if (!container) return;

    let liveRegion = document.getElementById(liveRegionId);

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = liveRegionId;
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      Object.assign(liveRegion.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: '0',
      });
      container.prepend(liveRegion);
    }
  }, []);

  useEffect(() => {
    const liveRegion = document.getElementById(
      'batch-redaction-history-toast-live-region'
    );

    if (!liveRegion) return;

    liveRegion.textContent = '';

    const activeToastMessage = isShowSuccessToast
      ? successMessage
      : isShowErrorToast
        ? rollbackErrorMessage
        : '';

    const applyDismissButtonAccessibility = () => {
      const dismissButton = Array.from(
        document.querySelectorAll<HTMLButtonElement>(
          '.toast-notification__container button'
        )
      ).find((button) => {
        const text = button.textContent?.replace(/\s+/g, ' ').trim();
        return text === '×' || !!button.querySelector('svg');
      });

      if (
        dismissButton &&
        dismissButton.getAttribute('aria-label') !== 'Dismiss notification'
      ) {
        dismissButton.setAttribute('aria-label', 'Dismiss notification');
      }
    };

    if (!activeToastMessage) {
      applyDismissButtonAccessibility();
      return;
    }

    const timer = window.setTimeout(() => {
      liveRegion.textContent = activeToastMessage;
      applyDismissButtonAccessibility();
    }, 50);

    const observer = new MutationObserver(() => {
      applyDismissButtonAccessibility();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      liveRegion.textContent = '';
    };
  }, [
    isShowSuccessToast,
    isShowErrorToast,
    successMessage,
    rollbackErrorMessage,
  ]);

  useEffect(() => {
    const container = document.querySelector(
      '[data-testid="redaction-history-grid"]'
    );
    if (!container) return;

    const setAttributeIfChanged = (
      element: Element,
      name: string,
      value: string
    ) => {
      if (element.getAttribute(name) !== value) {
        element.setAttribute(name, value);
      }
    };

    const applyAccessibility = () => {
      const emptyStateContainer = container.querySelector('.no-grid-overlay');

      if (emptyStateContainer) {
        const emptyStateIcons = emptyStateContainer.querySelectorAll('svg');

        emptyStateIcons.forEach((icon) => {
          setAttributeIfChanged(icon, 'aria-hidden', 'true');
          setAttributeIfChanged(icon, 'focusable', 'false');
        });
      }

      const rows = Array.from(
        container.querySelectorAll('[role="row"]')
      ).filter((row) => {
        const rowElement = row as HTMLElement;
        const cells = rowElement.querySelectorAll('[role="gridcell"]');
        return rowElement.offsetParent !== null && cells.length > 0;
      });

      rows.forEach((row) => {
        const rowElement = row as HTMLElement;
        const cells = rowElement.querySelectorAll('[role="gridcell"]');
        const firstCellText =
          cells[0]?.textContent?.replace(/\s+/g, ' ').trim() ?? '';

        const buttons = Array.from(
          rowElement.querySelectorAll('button')
        ).filter((button) => (button as HTMLElement).offsetParent !== null);

        buttons.forEach((button) => {
          const buttonElement = button as HTMLButtonElement;
          const buttonText = buttonElement.textContent
            ?.replace(/\s+/g, ' ')
            .trim();

          if (buttonText) {
            setAttributeIfChanged(
              buttonElement,
              'aria-label',
              `${buttonText}${firstCellText ? ` ${firstCellText}` : ''}`
            );
          }
        });
      });

      const paginationContainer = container.querySelector(
        '.MuiTablePagination-root'
      );

      if (!paginationContainer) return;

      const pageSizeSelect = paginationContainer.querySelector(
        '[aria-label="Rows per page"], .MuiTablePagination-select'
      );

      if (pageSizeSelect) {
        setAttributeIfChanged(pageSizeSelect, 'aria-label', 'Per page');
      }

      const paginationButtons = Array.from(
        paginationContainer.querySelectorAll('button')
      ).filter((button) => (button as HTMLElement).offsetParent !== null);

      paginationButtons.forEach((button) => {
        const buttonElement = button as HTMLButtonElement;
        const text = buttonElement.textContent?.replace(/\s+/g, ' ').trim();
        const ariaLabel = buttonElement.getAttribute('aria-label')?.trim();

        if (ariaLabel?.toLowerCase().includes('previous')) {
          setAttributeIfChanged(buttonElement, 'aria-label', 'Previous page');
          return;
        }

        if (ariaLabel?.toLowerCase().includes('next')) {
          setAttributeIfChanged(buttonElement, 'aria-label', 'Next page');
          return;
        }

        if (text && /^\d+$/.test(text)) {
          setAttributeIfChanged(buttonElement, 'aria-label', `Page ${text}`);

          if (
            buttonElement.getAttribute('aria-current') === 'true' ||
            buttonElement.getAttribute('aria-current') === 'page' ||
            buttonElement.getAttribute('aria-selected') === 'true'
          ) {
            setAttributeIfChanged(buttonElement, 'aria-current', 'page');
          }
        }
      });
    };

    const timer = window.requestAnimationFrame(applyAccessibility);
    const observer = new MutationObserver(() => {
      applyAccessibility();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.cancelAnimationFrame(timer);
      observer.disconnect();
    };
  }, [redactionData, totalRecords, pageNumber, isLoading]);

  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper) return;

    if (isZeroState) {
      wrapper.setAttribute('inert', '');
    } else {
      wrapper.removeAttribute('inert');
    }
  }, [isZeroState]);

  return (
    <>
      <ManageHeader
        isShowGroup={false}
        title={t('batchRedactions.historyGrid.batchRedactionhistory')}
        infoContentTitle={t(
          'batchRedactions.historyGrid.batchRedactionhistory'
        )}
        infoContent={t('batchRedactions.historyGrid.titleTooltip')}
      />

      <div data-testid="redaction-history-grid" ref={gridWrapperRef}>
        <ArrDataGrid
          data-testid="redaction-data-grid"
          rows={redactionData}
          columns={getProjectFieldListGridColumns({
            handleClickActions,
            handleGenerateReport,
          })}
          loading={isLoading}
          primaryKey="batchRedactionID"
          className={`redaction-history-grid ${
            redactionData.length === 0 ? 'fullHeight' : ''
          }`}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          onPageChange={handlePageChange}
          itemsPerPage={PAGE_SIZE}
          rowCount={totalRecords}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          hideFooter={totalRecords === 0}
          pageSizeOptions={[PAGE_SIZE]}
          noRowsOverlay={NoRowsOverlay}
          columnHeaderHeight={64}
        />
      </div>

      <ArrModal
        showModal={confirmModal}
        showClose={true}
        onClose={closeConfirmModal}
        title={
          cancelRollback
            ? `${t('batchRedactions.rollbackPopup.cancepPopupTitle')} # ${batchRedactionData?.batchRedactionID}`
            : t('batchRedactions.rollbackPopup.rollbackPopupTitle')
        }
        className="batch-redaction-model"
        dataTestId="keyword-add-edit-confirm-modal"
        buttons={[
          {
            id: 'confirm_cancel',
            label: t('common.cancel'),
            onClick: closeConfirmModal,
            variant: 'secondary',
            size: 'medium',
            dataTestId: 'kaec-cancel-btn',
          },
          {
            id: 'confirm_save',
            label: t('common.continue'),
            onClick: handleConfirmModal,
            variant: 'primary',
            size: 'medium',
            loading: rollbackLoading,
            dataTestId: 'kae-save-btn',
          },
        ]}
      >
        <Box className="keyword-confirm-modal-content">
          <Grid
            container
            spacing={12}
            className="typo-text body2_size glacier-border-dim"
          >
            {cancelRollback
              ? t('batchRedactions.rollbackPopup.cancelPopupContent')
              : `${t('batchRedactions.rollbackPopup.rollbackPopupContent')} ${formatDateTime(
                  batchRedactionData?.lastUpdatedOn
                )} 
by ${batchRedactionData?.fullName}.`}
          </Grid>
        </Box>
      </ArrModal>

      {isShowSuccessToast && (
        <ArrToastNotification
          message={successMessage}
          status={[ToastType.Success]}
          onClose={() => setIsShowSuccessToast(false)}
        />
      )}

      {/* Error Toast */}
      {isShowErrorToast && (
        <ArrToastNotification
          message={rollbackErrorMessage}
          status={[ToastType.Error]}
          onClose={() => setIsShowErrorToast(false)}
        />
      )}
    </>
  );
};

export {BatchRedactionHistoryGrid};
